import React, { useEffect, useState, useRef, useContext } from "react";
import SoftDivider from "../components/SoftDivider";
import ArticleCard from "../components/ArticleCard";
import AuthorPanel from "../components/AuthorPanel";
import DiscoverPanel from "../components/DiscoverPanel";
import Layout from "../components/Layout";
import { RecommendationService } from "../services/RecommendationService";
import { AuthContext } from "../context/AuthContext";

const API = "https://koufnere5g.execute-api.eu-west-2.amazonaws.com/prod/articles";

export default function Home() {
  const { user } = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const [breakingNews, setBreakingNews] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const [discoverArticles, setDiscoverArticles] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const FETCH_COOLDOWN = 60000; // 1 minute cooldown between fetches

  useEffect(() => {
    const fetchArticles = async () => {
      // Implement fetch cooldown to prevent excessive API calls
      const now = Date.now();
      if (now - lastFetchTime < FETCH_COOLDOWN && articles.length > 0) {
        console.log('Skipping fetch due to cooldown');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch regular articles
        const response = await fetch(API);
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const data = await response.json();
        const allArticles = Array.isArray(data) ? data : JSON.parse(data.body);
        
        // Get personalized recommendations
        const userId = user?.username || 'anonymous';
        const recommendations = await RecommendationService.getMainFeedRecommendations(userId);
        
        // If we have recommendations, use them to sort articles
        if (recommendations && recommendations.length > 0) {
          // Create a map of article IDs to their recommendation order
          const recommendationOrder = {};
          recommendations.forEach((rec, index) => {
            recommendationOrder[rec.articleId] = index;
          });
          
          // Sort articles based on recommendation order
          const sortedArticles = [...allArticles].sort((a, b) => {
            const aOrder = recommendationOrder[a.id] !== undefined ? recommendationOrder[a.id] : Number.MAX_SAFE_INTEGER;
            const bOrder = recommendationOrder[b.id] !== undefined ? recommendationOrder[b.id] : Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
          });
          
          setArticles(sortedArticles);
          setFilteredArticles(sortedArticles);
        } else {
          // If no recommendations, use default order
          setArticles(allArticles);
          setFilteredArticles(allArticles);
        }
        
        // Set breaking news (top 5 articles)
        setBreakingNews(allArticles.slice(0, 5));
        
        // Get discover panel recommendations
        const discoverRecs = await RecommendationService.getDiscoverRecommendations(userId);
        if (discoverRecs && discoverRecs.length > 0) {
          // Match recommendation IDs to actual articles
          const discoverArticlesList = discoverRecs.map(rec => {
            return allArticles.find(article => article.id === rec.articleId) || rec;
          }).filter(article => article);
          
          setDiscoverArticles(discoverArticlesList);
        } else {
          setDiscoverArticles(allArticles.slice(0, 5));
        }
        
        setError(null);
        setLastFetchTime(now);
      } catch (err) {
        console.error("Error fetching articles:", err);
        setError("Failed to load articles. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [user, lastFetchTime, articles.length, FETCH_COOLDOWN]); // Include all dependencies

  // Handle search/discovery
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredArticles(articles);
      return;
    }
    
    // Simple search implementation
    const filtered = articles.filter(article => 
      article.title.toLowerCase().includes(query.toLowerCase()) ||
      (article.content && article.content.toLowerCase().includes(query.toLowerCase())) ||
      (article.author && article.author.toLowerCase().includes(query.toLowerCase()))
    );
    
    setFilteredArticles(filtered);
  };

  // Track article views and get related articles
  const handleArticleClick = async (slug) => {
    const article = articles.find(a => a.slug === slug);
    
    if (article && user) {
      // Track the interaction
      RecommendationService.trackInteraction(
        user.username, 
        article.id, 
        'click'
      );
      
      // Get related articles
      const related = await RecommendationService.getRelatedArticles(article.id);
      if (related && related.length > 0) {
        // Match recommendation IDs to actual articles
        const relatedArticlesList = related.map(rec => {
          return articles.find(a => a.id === rec.articleId) || rec;
        }).filter(a => a);
        
        setRelatedArticles(relatedArticlesList);
      } else {
        // Fallback to default related articles
        setRelatedArticles(
          articles.filter(a => a.slug !== slug).slice(0, 3)
        );
      }
    }
    
    setOpenSlug(slug);
  };

  // Control body scrolling based on whether an article is open
  useEffect(() => {
    if (openSlug) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [openSlug]);

  const opened = articles.find((a) => a.slug === openSlug);
  const related = relatedArticles.length > 0 ? relatedArticles : 
    articles.filter((a) => a.slug !== openSlug).slice(0, 3);

  if (loading) {
    return (
      <Layout onSearch={handleSearch}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-xl text-gray-500">Loading articles...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout onSearch={handleSearch}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-xl text-red-500">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onSearch={handleSearch}>
      <div className="relative">
        {/* Main Feed */}
        <div 
          ref={containerRef}
          className="overflow-y-auto snap-y snap-mandatory h-[calc(100vh-4rem)]"
          style={{ 
            overflowY: openSlug ? 'hidden' : 'auto',
            height: 'calc(100vh - 4rem)',
            marginTop: '0',
            marginRight: '18rem' // Make space for the right panel
          }}
        >
          {filteredArticles.map((article, idx) => {
            const isOpen = openSlug === article.slug;
            return (
              <React.Fragment key={article.slug}>
                <div
                  className={`snap-start h-screen flex items-start pt-4 pl-4 pr-4`}
                  style={{ display: openSlug && !isOpen ? 'none' : 'flex' }}
                >
                  <ArticleCard
                    article={article}
                    isOpen={isOpen}
                    onClick={() => handleArticleClick(isOpen ? null : article.slug)}
                  />
                </div>
                {!openSlug && (idx + 1) % 4 === 0 && <SoftDivider />}
              </React.Fragment>
            );
          })}
          
          {filteredArticles.length === 0 && searchQuery && (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">
                No articles found matching "{searchQuery}"
              </div>
            </div>
          )}
        </div>
        
        {/* Right Panel - Either Discover or Author */}
        {openSlug ? (
          <AuthorPanel article={opened} related={related} />
        ) : (
          <DiscoverPanel 
            articles={discoverArticles.length > 0 ? discoverArticles : breakingNews} 
            onArticleClick={(slug) => handleArticleClick(slug)}
          />
        )}
      </div>
    </Layout>
  );
}